import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

function EnrollAllButton({ props, onDone }) {
  const eligible = props.filter((p) => p.status === "Eligible" && p.capacity).length;
  const enrollAll = async () => {
    try {
      const { data } = await api.post("/admin/properties/enroll-all");
      toast.success(`Enrolled ${data.enrolled} properties — everyone's in`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (eligible === 0) return null;
  return (
    <Button size="sm" className="sw-red hover:bg-red-700 text-white" onClick={enrollAll} data-testid="enroll-all-btn">
      Enroll all eligible ({eligible})
    </Button>
  );
}

function EditDialog({ prop, regionals, onClose, onSaved }) {
  const [f, setF] = useState({ capacity: prop.capacity ?? "", tier_override: "", regional: prop.regional || "", status: prop.status, location: prop.location });
  const save = async () => {
    try {
      const body = {
        capacity: f.capacity !== "" && parseInt(f.capacity, 10) !== prop.capacity ? parseInt(f.capacity, 10) : null,
        tier_override: f.tier_override || null,
        regional: f.regional !== prop.regional ? f.regional : null,
        status: f.status !== prop.status ? f.status : null,
        location: f.location !== prop.location ? f.location : null,
      };
      await api.put(`/admin/properties/${prop.id}`, body);
      toast.success("Property updated");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-property-dialog">
        <DialogHeader><DialogTitle className="font-display">{prop.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Capacity ({prop.capacity_type})</Label><Input type="number" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="edit-capacity" /></div>
          <div><Label className="text-xs">Tier override</Label>
            <Select value={f.tier_override} onValueChange={(v) => setF({ ...f, tier_override: v })}>
              <SelectTrigger data-testid="edit-tier"><SelectValue placeholder={prop.tier || "auto"} /></SelectTrigger>
              <SelectContent><SelectItem value="Small">Small</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Large">Large</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Regional</Label>
            <Select value={f.regional} onValueChange={(v) => setF({ ...f, regional: v })}>
              <SelectTrigger data-testid="edit-regional"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{regionals.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Eligible", "Enrolled", "Active", "Withdrawn", "Needs capacity"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="edit-location" /></div>
        </div>
        <Button onClick={save} className="sw-red hover:bg-red-700 text-white" data-testid="edit-save-btn">Save changes</Button>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ prop, regionals, onClose, onSaved }) {
  const [f, setF] = useState({ contact_name: prop.community_manager || "", contact_email: "", capacity: prop.capacity ?? "", regional: prop.regional || "" });
  const [result, setResult] = useState(null);
  const isReassign = !!prop.owner;
  const go = async () => {
    try {
      if (isReassign) {
        const { data } = await api.post(`/admin/properties/${prop.id}/reassign`, { email: f.contact_email, name: f.contact_name });
        setResult(data.temp_password ? data : null);
        toast.success("Ownership reassigned");
        if (!data.temp_password) onSaved();
      } else {
        const { data } = await api.post(`/admin/properties/${prop.id}/enroll`, { ...f, capacity: f.capacity ? parseInt(f.capacity, 10) : null });
        setResult(data);
        toast.success("Property enrolled");
      }
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && (result ? onSaved() : onClose())}>
      <DialogContent data-testid="enroll-dialog">
        <DialogHeader><DialogTitle className="font-display">{isReassign ? "Reassign" : "Enroll"} {prop.name}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-2" data-testid="enroll-result">
            <p className="text-sm">Account created for <b>{result.email}</b>. Share this temporary password (shown once):</p>
            <div className="bg-slate-100 rounded-md p-3 font-mono text-lg text-center" data-testid="temp-password">{result.temp_password}</div>
            <p className="text-xs text-slate-500">They must change it on first sign-in.</p>
            <Button onClick={onSaved} className="w-full" data-testid="enroll-done-btn">Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-xs">Contact name</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="enroll-name" /></div>
            <div><Label className="text-xs">Contact email</Label><Input type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="enroll-email" /></div>
            {!isReassign && (
              <>
                <div><Label className="text-xs">Capacity ({prop.capacity_type})</Label><Input type="number" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="enroll-capacity" /></div>
                <div><Label className="text-xs">Regional</Label>
                  <Select value={f.regional} onValueChange={(v) => setF({ ...f, regional: v })}>
                    <SelectTrigger data-testid="enroll-regional"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{regionals.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button onClick={go} className="w-full sw-red hover:bg-red-700 text-white" data-testid="enroll-submit-btn">{isReassign ? "Reassign ownership" : "Enroll & invite"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProperties() {
  const [props, setProps] = useState([]);
  const [regionals, setRegionals] = useState([]);
  const [q, setQ] = useState("");
  const [division, setDivision] = useState("all");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const [sortAccounts, setSortAccounts] = useState(null); // null | "asc" | "desc"

  const load = useCallback(() => {
    api.get("/admin/properties").then(({ data }) => setProps(data));
    api.get("/regionals").then(({ data }) => setRegionals(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = props.filter((p) =>
    (division === "all" || p.division === division) &&
    (status === "all" || p.status === status) &&
    (!q || p.name.toLowerCase().includes(q.toLowerCase()) || p.location.toLowerCase().includes(q.toLowerCase()) || (p.regional || "").toLowerCase().includes(q.toLowerCase()))
  ).sort((a, b) => sortAccounts ? (sortAccounts === "asc" ? a.user_count - b.user_count : b.user_count - a.user_count) || a.name.localeCompare(b.name) : 0);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input placeholder="Search name, city, regional…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="prop-search" />
        <Select value={division} onValueChange={setDivision}>
          <SelectTrigger className="w-36" data-testid="prop-filter-division"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">All divisions</SelectItem><SelectItem value="Student">Student</SelectItem><SelectItem value="Conventional">Conventional</SelectItem><SelectItem value="Support">Support</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="prop-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["Eligible", "Enrolled", "Active", "Withdrawn", "Needs capacity"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3">
          <EnrollAllButton props={props} onDone={load} />
          <span className="text-sm text-slate-500" data-testid="prop-count">{filtered.length} of {props.length}</span>
        </div>
      </div>
      <div className="border border-slate-200 rounded-md overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#f8f9fc] text-xs uppercase tracking-wide text-slate-500 sticky top-0">
            <tr><th className="px-3 py-2">Property</th><th className="px-3 py-2">Division</th><th className="px-3 py-2">Tier</th><th className="px-3 py-2">Capacity</th><th className="px-3 py-2">Regional</th><th className="px-3 py-2">Owner</th>
              <th className="px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => setSortAccounts(sortAccounts === "asc" ? "desc" : "asc")} data-testid="accounts-sort-header">
                Accounts {sortAccounts === "asc" ? "▲" : sortAccounts === "desc" ? "▼" : "↕"}
              </th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`prop-row-${p.id}`}>
                <td className="px-3 py-2"><div className="font-medium text-[#1a1f36]">{p.name}</div><div className="text-xs text-slate-400">{p.location}</div></td>
                <td className="px-3 py-2">{p.division}</td>
                <td className="px-3 py-2">{p.tier || <span className="text-[#c8102e] text-xs">—</span>}</td>
                <td className="px-3 py-2">{p.capacity ? `${p.capacity} ${p.capacity_type}` : <span className="text-[#c8102e] text-xs">{p.note || "needed"}</span>}</td>
                <td className="px-3 py-2 text-xs">{p.regional || "—"}</td>
                <td className="px-3 py-2 text-xs">{p.owner ? <span title={p.owner.email}>{p.owner.name}</span> : "—"}</td>
                <td className="px-3 py-2" data-testid={`prop-accounts-${p.id}`}>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.user_count === 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>{p.user_count}</span>
                </td>
                <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`prop-edit-${p.id}`}>Edit</Button>
                  {p.owner ? (
                    <Button size="sm" variant="ghost" className="text-[#c8102e]" onClick={() => setEnrolling(p)} data-testid={`prop-enroll-${p.id}`}>Reassign</Button>
                  ) : !["Enrolled", "Active"].includes(p.status) && (
                    <Button size="sm" variant="ghost" className="text-[#c8102e]" onClick={() => setEnrolling(p)} data-testid={`prop-enroll-${p.id}`}>Enroll</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <EditDialog prop={editing} regionals={regionals} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {enrolling && <EnrollDialog prop={enrolling} regionals={regionals} onClose={() => setEnrolling(null)} onSaved={() => { setEnrolling(null); load(); }} />}
    </div>
  );
}
